import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Search,
  Info,
} from "lucide-react";

const WordSearchVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Simple Success",
      board: [
        ["A", "B", "C", "E"],
        ["S", "F", "C", "S"],
        ["A", "D", "E", "E"],
      ],
      word: "ABCCED",
    },
    {
      id: 2,
      name: "Simple Failure",
      board: [
        ["A", "B", "C", "E"],
        ["S", "F", "C", "S"],
        ["A", "D", "E", "E"],
      ],
      word: "ABCB",
    },
    {
      id: 3,
      name: "Longer Word",
      board: [
        ["C", "A", "A"],
        ["A", "A", "A"],
        ["B", "C", "D"],
      ],
      word: "AAB",
    },
    {
      id: 4,
      name: "Requires Backtracking",
      board: [
        ["A", "B", "C", "E"],
        ["S", "F", "E", "S"],
        ["A", "D", "E", "E"],
      ],
      word: "ABCESEEEFS",
    },
    {
      id: 5,
      name: "Word Not Present",
      board: [
        ["a", "b"],
        ["c", "d"],
      ],
      word: "abcd",
    },
    {
      id: 6,
      name: "Single Character",
      board: [["a"]],
      word: "a",
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customBoard, setCustomBoard] = useState("");
  const [customWord, setCustomWord] = useState("");
  const [board, setBoard] = useState(defaultTestCases[0].board);
  const [word, setWord] = useState(defaultTestCases[0].word);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function exist(board, word):", indent: 0 },
    { line: "for r = 0 to R-1:", indent: 1 },
    { line: "for c = 0 to C-1:", indent: 2 },
    { line: "if backtrack(r, c, 0): return true", indent: 3 },
    { line: "return false", indent: 1 },
    { line: "", indent: 0 },
    { line: "function backtrack(r, c, index):", indent: 0 },
    { line: "// Base Case: Word found", indent: 1 },
    { line: "if index == word.length: return true", indent: 1 },
    { line: "// Check boundaries and character match", indent: 1 },
    { line: "if invalid(r,c) or board[r][c] != word[index]:", indent: 1 },
    { line: "return false", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Mark the path", indent: 1 },
    { line: "temp = board[r][c]", indent: 1 },
    { line: "board[r][c] = '#'", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Explore neighbors", indent: 1 },
    { line: "found = backtrack(r+1, c, index+1) or ...", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Unmark the path (backtrack)", indent: 1 },
    { line: "board[r][c] = temp", indent: 1 },
    { line: "return found", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (board, word) => {
    const allSteps = [];
    let stepCount = 0;
    const R = board.length;
    if (R === 0) return [];
    const C = board[0].length;
    let currentBoard = JSON.parse(JSON.stringify(board));

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        board: JSON.parse(JSON.stringify(currentBoard)),
        description,
        highlightLine,
        ...details,
      });
    };

    function backtrack(r, c, index, path) {
      // Base case: Word found
      if (index === word.length) {
        addStep(`Word "${word}" found!`, 8, {
          path,
          wordIndex: index,
          found: true,
        });
        return true;
      }

      // Check boundaries
      if (r < 0 || r >= R || c < 0 || c >= C) {
        addStep(`Out of bounds at [${r}][${c}]. Backtracking.`, 10, {
          path,
          wordIndex: index,
        });
        return false;
      }

      // Check character match
      if (currentBoard[r][c] !== word[index]) {
        addStep(
          `'${currentBoard[r][c]}' != '${word[index]}'. Backtracking.`,
          10,
          { path, wordIndex: index, currentCell: { r, c } }
        );
        return false;
      }

      const newPath = [...path, { r, c }];

      addStep(`Found '${word[index]}' at [${r}][${c}].`, 10, {
        path: newPath,
        wordIndex: index,
        currentCell: { r, c },
      });

      const temp = currentBoard[r][c];
      currentBoard[r][c] = "#"; // Mark
      addStep(`Marking [${r}][${c}] as visited.`, 15, {
        path: newPath,
        wordIndex: index,
        currentCell: { r, c },
      });

      const directions = [
        [0, 1],
        [1, 0],
        [0, -1],
        [-1, 0],
      ]; // R, D, L, U
      for (const [dr, dc] of directions) {
        addStep(`Exploring neighbor at [${r + dr}][${c + dc}].`, 18, {
          path: newPath,
          wordIndex: index + 1,
          currentCell: { r, c },
        });
        if (backtrack(r + dr, c + dc, index + 1, newPath)) {
          return true;
        }
      }

      currentBoard[r][c] = temp; // Backtrack
      addStep(`No path from [${r}][${c}]. Backtracking.`, 21, {
        path,
        wordIndex: index,
        currentCell: { r, c },
      });

      return false;
    }

    addStep("Start searching for the word.", 1);
    for (let r = 0; r < R; r++) {
      for (let c = 0; c < C; c++) {
        addStep(`Scanning for first letter '${word[0]}' at [${r}][${c}].`, 2, {
          scanCell: { r, c },
          wordIndex: 0,
        });
        if (board[r][c] === word[0]) {
          addStep(`Found '${word[0]}' at [${r}][${c}]. Starting search.`, 3, {
            scanCell: { r, c },
            wordIndex: 0,
          });
          if (backtrack(r, c, 0, [])) {
            addStep(`Word "${word}" found starting at [${r}][${c}].`, -1, {
              path: allSteps[allSteps.length - 1].path,
              found: true,
              isFinal: true,
            });
            return allSteps;
          }
        }
      }
    }

    addStep(`Word "${word}" not found in the board.`, 4, {
      found: false,
      isFinal: true,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(board, word);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [board, word]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setBoard(testCases[index].board);
    setWord(testCases[index].word);
  };

  const addCustomTestCase = () => {
    try {
      const parsedBoard = JSON.parse(customBoard);
      if (
        !Array.isArray(parsedBoard) ||
        !parsedBoard.every(
          (row) =>
            Array.isArray(row) &&
            row.every((cell) => typeof cell === "string" && cell.length === 1)
        )
      ) {
        throw new Error("Invalid board format.");
      }
      if (
        parsedBoard.length > 0 &&
        !parsedBoard.every((row) => row.length === parsedBoard[0].length)
      ) {
        throw new Error("All rows in the board must have the same length.");
      }
      if (customWord.trim().length === 0) {
        throw new Error("Word cannot be empty.");
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        board: parsedBoard,
        word: customWord.trim().toUpperCase(),
      };
      setTestCases([...testCases, newTestCase]);
      setCustomBoard("");
      setCustomWord("");
    } catch (error) {
      alert(
        `Invalid format. ${error.message} Use a JSON 2D array for board like [["A"],["B"]] and a string for word.`
      );
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Search className="text-blue-500" size={24} /> Word Search
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given a 2D board and a word, find if the word exists in the
                grid. The word can be constructed from letters of sequentially
                adjacent cells (horizontally or vertically). The same letter
                cell may not be used more than once.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">Word: "{tc.word}"</div>
                  </button>
                  {tc.id > 6 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customBoard}
                onChange={(e) => setCustomBoard(e.target.value)}
                placeholder='Board: [["A","B"],["C","D"]]'
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="text"
                value={customWord}
                onChange={(e) => setCustomWord(e.target.value)}
                placeholder="Word: ABCD"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>
            <div className="flex justify-center p-2 mb-4">
              {currentStepData?.board?.length > 0 ? (
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: `repeat(${
                      currentStepData.board[0]?.length || 1
                    }, minmax(0, 1fr))`,
                  }}
                >
                  {currentStepData.board.map((row, r) =>
                    row.map((cell, c) => {
                      const { scanCell, path, currentCell } = currentStepData;
                      let baseClass = "bg-gray-100 border-gray-300";
                      const isInPath = path?.some(
                        (p) => p.r === r && p.c === c
                      );

                      if (scanCell?.r === r && scanCell?.c === c) {
                        baseClass = "bg-yellow-300 border-yellow-500";
                      } else if (isInPath) {
                        baseClass = "bg-green-300 border-green-500";
                      } else if (currentCell?.r === r && currentCell?.c === c) {
                        baseClass = "bg-blue-300 border-blue-500 scale-110";
                      }

                      return (
                        <div
                          key={`${r}-${c}`}
                          className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 font-mono text-sm font-bold ${baseClass}`}
                        >
                          {cell}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No board to display.</p>
              )}
            </div>
            <div className="mt-4 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold mb-2">
                Searching for Word:
              </h4>
              <div className="flex flex-wrap gap-1 text-lg font-mono justify-center">
                {word.split("").map((char, idx) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 rounded ${
                      currentStepData?.wordIndex === idx
                        ? "bg-blue-500 text-white"
                        : currentStepData?.wordIndex > idx
                        ? "bg-green-200 text-green-800"
                        : "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {char}
                  </span>
                ))}
              </div>
            </div>
            {currentStepData?.isFinal && (
              <div
                className={`mt-3 p-3 rounded ${
                  currentStepData.found ? "bg-green-100" : "bg-red-100"
                }`}
              >
                <h4
                  className={`text-sm font-bold ${
                    currentStepData.found ? "text-green-900" : "text-red-900"
                  }`}
                >
                  {currentStepData.found ? "Success!" : "Failure"}
                </h4>
                <p
                  className={`text-xs mt-1 ${
                    currentStepData.found ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {currentStepData.found
                    ? `The word "${word}" was found.`
                    : `The word "${word}" could not be found.`}
                </p>
              </div>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="100"
                  max="1500"
                  step="100"
                  value={1600 - speed}
                  onChange={(e) => setSpeed(1600 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Backtracking Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              This problem is solved using a backtracking algorithm, a form of
              Depth-First Search (DFS). We iterate through each cell, and if it
              matches the first letter of the word, we start a DFS from there.
              During the DFS, we mark visited cells to avoid reuse and explore
              all four neighbors. If a path fails, we "backtrack" by unmarking
              the cell and trying a different path.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N * 3^L)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(L)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = number of cells, L = length of the word.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Backtracking is essential. When a path doesn't lead to the
                  solution, you must undo your choices (unmark the cell) to
                  allow other paths to use it.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Temporarily modifying the board (e.g., to '#') is a common way
                  to keep track of the current path's visited cells without
                  using extra memory.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The search can start from any cell in the grid that matches
                  the first letter of the word.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-yellow-300 border-2 border-yellow-500 rounded"></div>
                <span>Scanning for start letter</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-300 border-2 border-blue-500 rounded"></div>
                <span>Current DFS cell</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-green-300 border-2 border-green-500 rounded"></div>
                <span>Current valid path</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-100 border-2 border-gray-300 rounded flex items-center justify-center font-bold">
                  #
                </div>
                <span>Marked as visited (in path)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordSearchVisualizer;
